/**
 * A DM peer resolved from the wire's `p2p_chat.user_one` / `user_two` fields.
 */
export type P2PPeer = {
  username: string;
  profileImage: string | null;
};

/**
 * Parse a DM participant field. Two wire shapes exist:
 *
 * - Legacy Rust chat list: a JSON-object STRING — `'{"username":"bob","profile_image":"…"}'`.
 * - Umbra (current backend): a PLAIN username string — including the literal
 *   `"Deleted User"` for an anonymised peer (account deletion).
 *
 * NEVER throws. A plain (non-"{") string is taken as the username itself.
 * That fallback is load-bearing — the legacy chat-list sync builds its
 * result inside one Promise.all, and a throw (or a skipped chat) fails the
 * whole sync / gets the DM locally DELETED by removeDeletedChats, losing
 * history the deletion feature deliberately keeps.
 *
 * Returns null for non-string / empty input, and for a "{"-prefixed string
 * that doesn't parse to a usable peer — callers keep the chat and default
 * the name / skip the event (see the brace-branch comment below).
 */
export function parseP2PPeer(raw: unknown): P2PPeer | null {
  if (typeof raw !== "string") {
    return null;
  }
  // Whitespace/BOM-safe: a stray leading space must not demote a JSON peer
  // payload to the plain-string branch (whose fallback would then use the
  // whole JSON text as a username).
  const value = raw.trim();
  if (value.length === 0) {
    return null;
  }

  // Legacy JSON-object shape. A real username can never start with "{"
  // (charset /^[a-z0-9._-]+$/), so the prefix check cannot misfire — and for
  // the same reason a "{"-prefixed string must NEVER fall through to the
  // plain-username branch: malformed/truncated peer JSON would otherwise
  // become the DM's display name and an avatar-map key. Returning null keeps
  // the chat itself intact (callers default the name / skip the event).
  if (value.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(value);
      const { username, profile_image } = (parsed ?? {}) as {
        username?: unknown;
        profile_image?: unknown;
      };
      if (typeof username === "string" && username) {
        return {
          username,
          profileImage:
            typeof profile_image === "string" && profile_image
              ? profile_image
              : null,
        };
      }
    } catch {
      // Truncated/invalid JSON — handled by the null below.
    }
    return null;
  }

  // Umbra shape: the string IS the username (or the deleted-user label).
  return { username: value, profileImage: null };
}
